import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api';
import { queryKeys } from '@/api/query-keys';
import { useUiPrefs } from '@/store/ui-prefs';
import { Avatar, Button, Icon, Modal, Textarea } from '@/ui';
import type { Me } from '@/types/api';
import { isNavEnabled } from './nav';
import { AGENT_ENABLED } from '@/config/features';
import { SearchBar } from './SearchBar';

interface TopbarProps {
  me: Me;
}

export function Topbar({ me }: TopbarProps) {
  const setTweaksOpen = useUiPrefs((s) => s.setTweaksOpen);
  const setChatOpen = useUiPrefs((s) => s.setChatOpen);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);

  // Poll unread notifications for the bell badge. 60s cadence keeps the
  // load light; messages page refetches on visit anyway.
  const notificationsQ = useQuery({
    queryKey: queryKeys.notifications.all,
    queryFn: () => api.notifications.list(),
    refetchInterval: 1000 * 60,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 30,
  });
  const unreadCount = (notificationsQ.data ?? []).filter((n) => !n.read).length;
  const messagesEnabled = isNavEnabled('messages');

  const logout = useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  return (
    <header
      style={{
        height: 56,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--apm-inner-gap)',
        padding: '0 var(--apm-page-pad)',
        borderBottom: '1px solid var(--apm-line)',
        background: 'var(--apm-bg-2)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <SearchBar />

      <div style={{ flex: 1 }} />

      <IconButton
        title={messagesEnabled
          ? (unreadCount > 0 ? `${unreadCount} unread message${unreadCount === 1 ? '' : 's'}` : 'Notifications')
          : 'Messages (disabled)'}
        iconName="bell"
        onClick={messagesEnabled ? () => navigate('/messages') : undefined}
        badge={messagesEnabled ? unreadCount : undefined}
        disabled={!messagesEnabled}
      />
      {AGENT_ENABLED && (
        <IconButton
          title="Atlas chat"
          iconName="sparkle"
          onClick={() => setChatOpen(true)}
        />
      )}
      <IconButton
        title="Theme & density"
        iconName="settings"
        onClick={() => setTweaksOpen(true)}
      />

      <div
        style={{
          height: 24,
          width: 1,
          background: 'var(--apm-line)',
          margin: '0 4px',
        }}
      />

      <button
        onClick={() => setProfileOpen(true)}
        title="Edit your profile"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '4px 8px 4px 4px',
          borderRadius: 'var(--apm-radius)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Avatar
          person={{ id: me.id, name: me.name, title: me.role }}
          size={28}
        />
        <div style={{ lineHeight: 1.1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--apm-ink)' }}>
            {me.name}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--apm-muted)' }}>{me.role}</div>
        </div>
      </button>
      <IconButton
        title="Log out"
        iconName="logout"
        onClick={() => logout.mutate()}
      />

      {profileOpen && (
        <ProfileModal me={me} onClose={() => setProfileOpen(false)} />
      )}
    </header>
  );
}

// Per-user profile editor. Today it's just the personal-context blob the
// chat agent reads from; more fields can join later (display name override,
// timezone, …). Saves go to PATCH /api/auth/me/personal-context.
function ProfileModal({ me, onClose }: { me: Me; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(me.personal_context ?? '');
  const save = useMutation({
    mutationFn: (text: string) => api.auth.updatePersonalContext(text),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.auth.me, updated);
      onClose();
    },
  });
  const dirty = (me.personal_context ?? '') !== draft;
  return (
    <Modal open onClose={onClose} width={580}>
      <div style={{ padding: 'var(--apm-card-pad)' }}>
        <div
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: 'var(--apm-muted)',
            marginBottom: 4,
          }}
        >
          Your profile
        </div>
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--apm-font-display)',
            fontWeight: 400,
            fontSize: 'var(--apm-text-xl)',
            lineHeight: 1.2,
          }}
        >
          Personal context for Atlas
        </h2>
        <p
          style={{
            fontSize: 13.5,
            color: 'var(--apm-ink-2)',
            lineHeight: 1.55,
            margin: '12px 0 8px',
          }}
        >
          Tell Atlas how you like to work. The chat agent reads this on every
          turn for your conversations — your tech preferences, hard rules,
          working hours, anything you'd otherwise have to repeat. Stays on
          your account; not visible to other team members.
        </p>
        <Textarea
          value={draft}
          onChange={setDraft}
          rows={10}
          placeholder={
            'e.g. "I develop in Python with Streamlit and FastAPI. I dislike '
            + 'corporate jargon. I have kids and block 5–8 PM on weeks they are '
            + 'with me. I am Quality and Regulatory at a medical-device company, '
            + 'so flag PHI risks early."'
          }
        />
        {save.isError && (
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '8px 12px',
              background: 'var(--apm-risk-soft)',
              color: 'var(--apm-risk)',
              borderRadius: 'var(--apm-radius)',
              fontSize: 12.5,
            }}
          >
            Couldn't save. Try again.
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            marginTop: 16,
          }}
        >
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate(draft.trim())}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function IconButton({
  iconName,
  title,
  onClick,
  badge,
  disabled,
}: {
  iconName: Parameters<typeof Icon>[0]['name'];
  title: string;
  onClick?: () => void;
  badge?: number;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-label={title}
      aria-disabled={disabled}
      disabled={disabled}
      style={{
        height: 32,
        width: 32,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        borderRadius: 'var(--apm-radius)',
        color: 'var(--apm-ink-2)',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = 'var(--apm-bg-sunken)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <Icon name={iconName} size={16} />
      {badge != null && badge > 0 && (
        <span
          style={{
            position: 'absolute',
            top: 3,
            right: 3,
            minWidth: 14,
            height: 14,
            padding: '0 3px',
            borderRadius: 999,
            background: 'var(--apm-accent)',
            color: '#fff',
            fontSize: 9.5,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            boxShadow: '0 0 0 2px var(--apm-bg-2)',
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
